/**
 * Unified input: keyboard + mouse, touch (virtual stick + buttons + look-drag),
 * and gamepad, mapped to a small set of semantic actions.
 *
 * Stages read:
 *   input.move        analog move vector, x = right, y = forward/up, length <= 1
 *   input.look        look delta (normalized screen units). Inside update() it is the delta for
 *                     this fixed step; inside frame() it is the delta for this render frame —
 *                     so cameras can read it in either place without double counting (just
 *                     don't apply it in BOTH).
 *   input.pointer     pointer position in NDC (-1..1) + whether it is active (desktop aim)
 *   input.down(a)     action held
 *   input.pressed(a)  action went down since last fixed update
 *   input.released(a) action went up since last fixed update
 *
 * The Game calls input.endFrame() after each fixed update so pressed/released are
 * edge-triggered exactly once per simulation step.
 */
import { Vector2 } from 'three';

export type Action = 'attack' | 'dash' | 'jump' | 'ability' | 'interact' | 'pause';
export type InputDevice = 'keyboard' | 'touch' | 'gamepad';

/**
 * Two-handed layouts — whichever hand is on movement, the other has an action cluster:
 *  - WASD + mouse:   Space jump · Shift dash · F / left-click attack · Q ability · E interact
 *  - WASD + JKL:     J attack · K dash · L ability · I interact (right hand)
 *  - Arrows + ZXCV:  Z attack · X dash · C ability · V interact (left hand)
 *  - Arrows + right: / or Right-Ctrl attack · Right-Shift dash · . ability · Enter interact
 */
export const KEY_MAP: Record<string, Action> = {
  Space: 'jump',
  ShiftLeft: 'dash',
  ShiftRight: 'dash',
  KeyK: 'dash',
  KeyX: 'dash',
  KeyJ: 'attack',
  KeyF: 'attack',
  KeyZ: 'attack',
  Slash: 'attack',
  ControlRight: 'attack',
  KeyQ: 'ability',
  KeyL: 'ability',
  KeyC: 'ability',
  Period: 'ability',
  KeyE: 'interact',
  KeyI: 'interact',
  KeyV: 'interact',
  Enter: 'interact',
  Escape: 'pause',
  KeyP: 'pause',
};

const MOVE_KEYS: Record<string, [number, number]> = {
  KeyW: [0, 1],
  ArrowUp: [0, 1],
  KeyS: [0, -1],
  ArrowDown: [0, -1],
  KeyA: [-1, 0],
  ArrowLeft: [-1, 0],
  KeyD: [1, 0],
  ArrowRight: [1, 0],
};

/** Standard gamepad mapping button indices. */
const PAD_MAP: Array<[number, Action]> = [
  [0, 'jump'], // A / Cross
  [1, 'dash'], // B / Circle
  [2, 'attack'], // X / Square
  [3, 'ability'], // Y / Triangle
  [5, 'attack'], // RB
  [4, 'dash'], // LB
  [7, 'attack'], // RT
  [6, 'dash'], // LT
  [9, 'pause'], // Start
  [8, 'interact'], // Select/Back
  [10, 'interact'], // L3
];

const ACTIONS: Action[] = ['attack', 'dash', 'jump', 'ability', 'interact', 'pause'];

export class Input {
  /** Smoothed analog move vector (keyboard input eases between directions; touch/pad are analog). */
  readonly move = new Vector2();
  /** Unsmoothed digital/analog move vector, for stages that want raw input. */
  readonly moveRaw = new Vector2();
  /** Keyboard smoothing time constant (s). 0 = off. */
  keyboardSmoothing = 0.07;
  private kbSmooth = new Vector2();
  private lookStep = new Vector2();
  private lookFrame = new Vector2();
  private lookAccumFrame = new Vector2();
  private phase: 'step' | 'frame' = 'frame';
  readonly pointer = { ndc: new Vector2(), active: false, down: false };
  device: InputDevice = 'keyboard';
  /** Sensitivity multiplier from settings. */
  sensitivity = 1;
  invertY = false;
  /** When true, clicking the canvas requests pointer lock (3D stages). */
  pointerLockPreferred = false;
  /** Set false while menus are open so gameplay doesn't react. */
  enabled = true;

  private held = new Set<Action>();
  private sources = new Map<Action, Set<string>>();
  private pressedSet = new Set<Action>();
  /** Fixed-step index of the last press per action, for input buffering (cleared by consume()).
   *  Measured in simulation steps (not wall-clock) so a frame hitch can't expire a press before
   *  the stage ever sees it. */
  private pressStep = new Map<Action, number>();
  /** Number of fixed steps begun so far. */
  private steps = 0;
  private releasedSet = new Set<Action>();
  private keysDown = new Set<string>();
  private touchMove = new Vector2();
  private touchMoveActive = false;
  private padMove = new Vector2();
  private padPrev: boolean[] = [];
  private lookAccum = new Vector2();
  private listeners: Array<() => void> = [];
  /** Callbacks fired immediately on pause press, even while gameplay input is disabled. */
  onPause?: () => void;

  constructor(private readonly target: HTMLElement) {
    for (const a of ACTIONS) this.sources.set(a, new Set());
    this.bind(window, 'keydown', this.onKeyDown as EventListener);
    this.bind(window, 'keyup', this.onKeyUp as EventListener);
    this.bind(window, 'blur', () => this.releaseAll());
    this.bind(target, 'pointermove', this.onPointerMove as EventListener);
    this.bind(target, 'pointerdown', this.onPointerDown as EventListener);
    this.bind(window, 'pointerup', this.onPointerUp as EventListener);
    this.bind(target, 'contextmenu', (e) => e.preventDefault());
    this.bind(document, 'mousemove', this.onMouseMove as EventListener);
  }

  private bind(el: EventTarget, type: string, fn: EventListener): void {
    el.addEventListener(type, fn, { passive: false });
    this.listeners.push(() => el.removeEventListener(type, fn));
  }

  dispose(): void {
    for (const off of this.listeners) off();
    this.listeners = [];
  }

  // ---- public query API -------------------------------------------------

  down(a: Action): boolean {
    return this.enabled && this.held.has(a);
  }

  pressed(a: Action): boolean {
    return this.enabled && this.pressedSet.has(a);
  }

  /**
   * Input buffering for responsive controls: true if the action was pressed within the last
   * `windowSec` seconds and hasn't been consumed yet. Use it for actions gated by cooldowns or
   * ground checks (attack, dash, jump) so a press made slightly early still fires:
   *   if (cooldown <= 0 && input.buffered('attack')) { input.consume('attack'); attack(); }
   */
  buffered(a: Action, windowSec = 0.15): boolean {
    if (!this.enabled) return false;
    const at = this.pressStep.get(a);
    if (at === undefined) return false;
    // Age in fixed steps since the press (the first step after a press has age 1).
    return this.steps - at <= Math.ceil(windowSec * 60) + 1;
  }

  /** Mark a buffered press as used so it can't fire twice. */
  consume(a: Action): void {
    this.pressStep.delete(a);
    this.pressedSet.delete(a);
  }

  released(a: Action): boolean {
    return this.enabled && this.releasedSet.has(a);
  }

  /** Look delta for the current phase (fixed step inside update(), render frame inside frame()). */
  get look(): Vector2 {
    return this.phase === 'step' ? this.lookStep : this.lookFrame;
  }

  /** Called by Game once per fixed step AFTER the stage update. */
  endFrame(): void {
    this.pressedSet.clear();
    this.releasedSet.clear();
    this.lookStep.set(0, 0);
    this.phase = 'frame';
  }

  /** Called by Game once per render frame BEFORE stage.frame(). */
  beginRender(): void {
    this.phase = 'frame';
    this.lookFrame.copy(this.lookAccumFrame).multiplyScalar(this.sensitivity);
    if (this.invertY) this.lookFrame.y *= -1;
    this.lookAccumFrame.set(0, 0);
    if (!this.enabled) this.lookFrame.set(0, 0);
  }

  /** Called by Game once per render frame AFTER stage.frame(). */
  endRender(): void {
    this.lookFrame.set(0, 0);
  }

  /** Called by Game once per fixed step BEFORE the stage update. */
  beginFrame(): void {
    this.steps++;
    this.phase = 'step';
    this.pollGamepad();
    // Combine move sources; touch/gamepad override keyboard when active.
    let x = 0;
    let y = 0;
    for (const code of this.keysDown) {
      const m = MOVE_KEYS[code];
      if (m) {
        x += m[0];
        y += m[1];
      }
    }
    const kb = new Vector2(x, y);
    if (kb.lengthSq() > 1) kb.normalize();
    // Keyboard is digital (8 directions): ease the vector so direction changes curve naturally
    // instead of snapping, while staying responsive (~70 ms).
    const k = this.keyboardSmoothing > 0 ? 1 - Math.exp(-(1 / 60) / this.keyboardSmoothing) : 1;
    this.kbSmooth.lerp(kb, k);
    if (kb.lengthSq() === 0 && this.kbSmooth.lengthSq() < 1e-4) this.kbSmooth.set(0, 0);
    if (this.touchMoveActive) {
      this.move.copy(this.touchMove);
      this.moveRaw.copy(this.touchMove);
    } else if (this.padMove.lengthSq() > 0.02) {
      this.move.copy(this.padMove);
      this.moveRaw.copy(this.padMove);
    } else {
      this.move.copy(this.kbSmooth);
      this.moveRaw.copy(kb);
    }
    if (!this.enabled) {
      this.move.set(0, 0);
      this.moveRaw.set(0, 0);
      this.kbSmooth.set(0, 0);
    }
    this.lookStep.copy(this.lookAccum).multiplyScalar(this.sensitivity);
    if (this.invertY) this.lookStep.y *= -1;
    this.lookAccum.set(0, 0);
    if (!this.enabled) this.lookStep.set(0, 0);
  }

  // ---- programmatic (touch UI, QA automation) --------------------------

  /** Press/release an action from a named source (e.g. 'touch:attack'). */
  setAction(a: Action, isDown: boolean, source = 'virtual'): void {
    const set = this.sources.get(a)!;
    const was = set.size > 0;
    if (isDown) set.add(source);
    else set.delete(source);
    const now = set.size > 0;
    if (now && !was) {
      this.held.add(a);
      this.pressedSet.add(a);
      this.pressStep.set(a, this.steps);
      if (a === 'pause') this.onPause?.();
    } else if (!now && was) {
      this.held.delete(a);
      this.releasedSet.add(a);
    }
  }

  /** Virtual stick value (touch UI or automation). Pass null to release. */
  setVirtualMove(v: Vector2 | null): void {
    if (v) {
      this.touchMove.copy(v);
      if (this.touchMove.lengthSq() > 1) this.touchMove.normalize();
      this.touchMoveActive = true;
    } else {
      this.touchMove.set(0, 0);
      this.touchMoveActive = false;
    }
  }

  /** Add look delta in normalized units (1.0 ≈ a full-screen-width drag). */
  addLook(dx: number, dy: number): void {
    this.lookAccum.x += dx;
    this.lookAccum.y += dy;
    this.lookAccumFrame.x += dx;
    this.lookAccumFrame.y += dy;
  }

  releaseAll(): void {
    this.keysDown.clear();
    this.kbSmooth.set(0, 0);
    for (const a of ACTIONS) {
      const set = this.sources.get(a)!;
      if (set.size) {
        set.clear();
        this.held.delete(a);
        this.releasedSet.add(a);
      }
    }
    this.setVirtualMove(null);
  }

  // ---- DOM handlers -----------------------------------------------------

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    this.device = 'keyboard';
    if (MOVE_KEYS[e.code]) {
      this.keysDown.add(e.code);
      if (e.code.startsWith('Arrow')) e.preventDefault();
    }
    const a = KEY_MAP[e.code];
    if (a) {
      if (e.code === 'Space' || e.code === 'Tab' || e.code === 'Slash') e.preventDefault();
      if (!e.repeat) this.setAction(a, true, `key:${e.code}`);
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keysDown.delete(e.code);
    const a = KEY_MAP[e.code];
    if (a) this.setAction(a, false, `key:${e.code}`);
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') return;
    const r = this.target.getBoundingClientRect();
    this.pointer.ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this.pointer.active = true;
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (document.pointerLockElement === this.target) {
      const w = Math.max(1, window.innerWidth);
      this.addLook(e.movementX / w, e.movementY / w);
    } else if (this.pointer.down && this.rightDragging) {
      const w = Math.max(1, window.innerWidth);
      this.addLook(e.movementX / w, e.movementY / w);
    }
  };

  private rightDragging = false;

  private onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') {
      this.device = 'touch';
      return;
    }
    this.device = 'keyboard';
    this.pointer.down = true;
    if (e.button === 0) {
      if (this.pointerLockPreferred && document.pointerLockElement !== this.target && this.enabled) {
        this.target.requestPointerLock?.()?.catch?.(() => undefined);
      }
      this.setAction('attack', true, 'mouse:0');
    } else if (e.button === 2) {
      this.rightDragging = true;
      if (!this.pointerLockPreferred) this.setAction('dash', true, 'mouse:2');
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') return;
    this.pointer.down = false;
    if (e.button === 0) this.setAction('attack', false, 'mouse:0');
    if (e.button === 2) {
      this.rightDragging = false;
      this.setAction('dash', false, 'mouse:2');
    }
  };

  exitPointerLock(): void {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private pollGamepad(): void {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    const pad = Array.from(pads ?? []).find((p) => p && p.connected);
    if (!pad) {
      this.padMove.set(0, 0);
      return;
    }
    const dz = (v: number) => (Math.abs(v) < 0.15 ? 0 : v);
    this.padMove.set(dz(pad.axes[0] ?? 0), -dz(pad.axes[1] ?? 0));
    if (this.padMove.lengthSq() > 1) this.padMove.normalize();
    const lx = dz(pad.axes[2] ?? 0);
    const ly = dz(pad.axes[3] ?? 0);
    if (lx || ly) this.addLook(lx * 0.02, ly * 0.02);
    if (this.padMove.lengthSq() > 0.02 || lx || ly) this.device = 'gamepad';
    const now: boolean[] = pad.buttons.map((b) => b.pressed);
    for (const [idx, action] of PAD_MAP) {
      const d = !!now[idx];
      if (d !== !!this.padPrev[idx]) {
        this.setAction(action, d, `pad:${idx}`);
        if (d) this.device = 'gamepad';
      }
    }
    // D-pad acts as move when sticks idle.
    if (this.padMove.lengthSq() < 0.02) {
      const dx = (now[15] ? 1 : 0) - (now[14] ? 1 : 0);
      const dy = (now[12] ? 1 : 0) - (now[13] ? 1 : 0);
      if (dx || dy) this.padMove.set(dx, dy).normalize();
    }
    this.padPrev = now;
  }
}
